import React, { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import axios from "axios";
import { saveToLocalStorage } from "./misc";
import constants from "./constants";

const sessionContext = React.createContext(null);

export function useSessionData(path, defaultValue, getAllContext) {
  const context = useContext(sessionContext);
  const { session } = context || {};
  if (getAllContext) {
    return context;
  }
  if (session !== undefined) {
    if (path == null) {
      return session;
    } else {
      return get(session, path, defaultValue);
    }
  } else {
    return defaultValue;
  }
}

export function withSessionData(Component) {
  return props => (
    <sessionContext.Consumer>
      {sessionProps => (
        <Component session={sessionProps ? sessionProps.session : null} {...props} />
      )}
    </sessionContext.Consumer>
  );
}

export async function getSessionData(sessionId) {
  const sessionUrl = `/api/sessions/${sessionId}`;
  try {
    const response = await axios.get(sessionUrl);
    if (response.status === 200) {
      return response.data;
    } else {
      console.error("Failed to fetch session data.");
    }
  } catch (error) {
    console.error("Failed to fetch session data, ", error);
  }
  // Delete browser storage if there is no session with given sessionId
  if (window.sessionStorage) {
    window.sessionStorage.clear();
  }
  if (window.localStorage) {
    window.localStorage.clear();
  }
  return undefined;
}
// TODO: this is problematic since with session context and configuration state we have
// two sources of truth, we should instead merge session and configuration so we don't have
// any params overlapping, and we have a params priority of
// urlParams
// sessionParams
// sessionStorageParams
// externalConfiguration (api) params
// meaning that sessionStorageParams override externalConfiguration but urlParams override everything
// we're doing this merging in the configurer, BUT if we use the useSession or withSession
// the merging logic does not happen
export function SessionDataProvider({ sessionId, children }) {
  const [session, setSession] = useState(null);
  useEffect(() => {
    const exec = async () => {
      if (sessionId) {
        try {
          const response = await getSessionData(sessionId);
          if (response) {
            setSession(response);
            saveToLocalStorage(
              constants.LOCAL_STORAGE_KEYS.SESSION_ID,
              sessionId,
              window.__additionalData.SESSION_EXPIRATION_TIME || 0
            );
          }
        } catch (error) {}
      }
    };
    exec();
  }, [sessionId]);

  const removeSessionData = useCallback(() => {
    setSession(null);
  }, []);

  if (!sessionId || session) {
    return (
      <sessionContext.Provider value={{ session, removeSessionData }}>
        {children}
      </sessionContext.Provider>
    );
  } else {
    return <>{children}</>;
  }
}

SessionDataProvider.propTypes = {
  sessionId: PropTypes.string,
};
